import { z } from "zod";
import {
  ApprovalStatus,
  ProjectStatus,
  UserRole,
  ProjectRevisionType,
} from "./enums";

// Authentication schemas
export const UserSchema = z.object({
  id: z.string().optional(),
  email: z.string().email("Invalid email address"),
  name: z.string().min(2, "Name must be at least 2 characters"),
  role: z.nativeEnum(UserRole),
  isActive: z.boolean().default(true),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
});

export const LoginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(1, "Password is required"),
});

export const RegisterSchema = z.object({
  email: z.string().email("Invalid email address"),
  name: z.string().min(2, "Name must be at least 2 characters"),
  password: z.string().min(6, "Password must be at least 6 characters"),
  role: z.nativeEnum(UserRole),
});

export const RefreshTokenSchema = z.object({
  refreshToken: z.string().min(1, "Refresh token is required"),
});

// Base schemas
export const EmployeeSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(2, "Name must be at least 2 characters"),
  email: z.string().email("Invalid email address"),
  department: z.string().min(1, "Department is required"),
  hourlyRate: z.number().positive("Hourly rate must be positive"),
  internalEmployeeId: z.string().optional(),
  isActive: z.boolean().optional().default(true),
  isDeleted: z.boolean().default(false),
  isArchived: z.boolean().default(false),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
});

export const ProjectSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1, "Project name is required"),
  clientName: z.string().min(1, "Client name is required"),
  description: z.string().optional(),
  internalProjectId: z.number().positive().nullable().optional(),
  startDate: z.date(),
  handlerMemberId: z.string().min(1, "Handler member is required"),
  salesTeamMemberId: z.string().optional(),
  createdBy: z.string().optional(),
  status: z.nativeEnum(ProjectStatus).default(ProjectStatus.ACTIVE),
  isDeleted: z.boolean().default(false),
  isArchived: z.boolean().default(false),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
});

export const OvertimeEmployeeSchema = z.object({
  employeeId: z.string().min(1, "Employee ID is required"),
  hours: z.number().positive("Hours must be positive"),
  rate: z.number().positive("Rate must be positive").optional(),
  total: z.number().positive("Total must be positive").optional(),
  notes: z.string().optional(),
  paymentStatus: z.boolean().default(false),
  paymentUpdatedAt: z.date().optional(),
});

export const ProjectRevisionHistorySchema = z.object({
  id: z.string().optional(),
  projectId: z.string().min(1, "Project ID is required"),
  revisionType: z.nativeEnum(ProjectRevisionType),
  changeDescription: z.string().min(1, "Change description is required"),
  changeDetails: z.record(z.any()).optional(), // JSON field for flexible change data
  overtimeEntryId: z.string().optional(), // Link to overtime entry if applicable
  changedBy: z.string().min(1, "Changed by user ID is required"),
  changedAt: z.date().optional(),
  changedByRef: z
    .object({
      id: z.string(),
      name: z.string(),
      email: z.string(),
    })
    .optional(),
});

export const OvertimeEntrySchema = z.object({
  id: z.string().optional(),
  projectId: z.string().min(1, "Project ID is required"),
  notes: z.string().optional(),
  date: z.date(),
  approvalStatus: z.nativeEnum(ApprovalStatus).default(ApprovalStatus.PENDING),
  rejectionComment: z.string().optional(),
  paymentStatus: z.boolean().default(false),
  paymentUpdatedAt: z.date().optional(),
  employees: z
    .array(OvertimeEmployeeSchema)
    .min(1, "At least one employee is required"),
  projectRevisionHistories: z.array(ProjectRevisionHistorySchema).optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
});

// Schema for the last overtime history entry with overtime entry reference
export const LastOvertimeHistorySchema = ProjectRevisionHistorySchema.extend({
  overtimeEntry: z.object({
    id: z.string(),
    date: z.date(),
    notes: z.string().optional(),
  }),
});

// Extended project schema for API responses that includes lastOvertimeHistory
export const ProjectMetadataSchema = z.object({
  id: z.string().optional(),
  projectId: z.string().min(1, "Project ID is required"),

  // Approved overtime counts
  totalApprovedOvertimeEntries: z.number().default(0),
  pendingApprovedOvertimeEntries: z.number().default(0),

  // Approved overtime hours and amounts
  totalApprovedHours: z.number().default(0),
  totalApprovedAmount: z.number().default(0),

  // Payment status for approved overtime
  paymentApprovedAmount: z.number().default(0),
  paymentPendingAmount: z.number().default(0),
  paymentApprovedCount: z.number().default(0),
  paymentPendingCount: z.number().default(0),

  // Pending overtime (not yet approved)
  totalPendingHours: z.number().default(0),
  totalPendingAmount: z.number().default(0),

  paymentUpdatedAt: z.date().optional(),
  updatedAt: z.date().optional(),
});

export const ProjectWithLastOvertimeHistorySchema = ProjectSchema.extend({
  lastOvertimeHistory: LastOvertimeHistorySchema.optional(),
  metadata: ProjectMetadataSchema.optional(),
});

// Type exports
export type User = z.infer<typeof UserSchema>;
export type LoginRequest = z.infer<typeof LoginSchema>;
export type RegisterRequest = z.infer<typeof RegisterSchema>;
export type RefreshTokenRequest = z.infer<typeof RefreshTokenSchema>;
export type Employee = z.infer<typeof EmployeeSchema>;
export type Project = z.infer<typeof ProjectSchema>;
export type OvertimeEmployee = z.infer<typeof OvertimeEmployeeSchema>;

export type OvertimeEntry = z.infer<typeof OvertimeEntrySchema>;
export type ProjectRevisionHistory = z.infer<
  typeof ProjectRevisionHistorySchema
>;
export type LastOvertimeHistory = z.infer<typeof LastOvertimeHistorySchema>;
export type ProjectMetadata = z.infer<typeof ProjectMetadataSchema>;
export type ProjectWithLastOvertimeHistory = z.infer<
  typeof ProjectWithLastOvertimeHistorySchema
>;

// Standardized API Response types
export interface ApiError {
  statusCode: HttpStatus;
  code: string;
  message: string;
  details?: Record<string, any>;
  stack?: string;
}

export interface ApiResponse<T = any, M = any> {
  success: boolean;
  data?: T;
  error?: ApiError;
  message?: string;
  timestamp: string;
  path?: string;
  method?: string;
  meta?: M;
}

export interface PaginationInfo {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface PaginatedResponse<T> extends ApiResponse<T[], {}> {
  pagination: PaginationInfo;
}

export interface ListResponse<T>
  extends ApiResponse<
    T[],
    {
      count: number;
      total: number;
    }
  > {}

// HTTP Status codes for reference
export enum HttpStatus {
  OK = 200,
  CREATED = 201,
  NO_CONTENT = 204,
  BAD_REQUEST = 400,
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
  NOT_FOUND = 404,
  CONFLICT = 409,
  UNPROCESSABLE_ENTITY = 422,
  INTERNAL_SERVER_ERROR = 500,
  BAD_GATEWAY = 502,
  SERVICE_UNAVAILABLE = 503,
}

// JWT Payload interface
export interface JWTPayload {
  userId: string;
  email: string;
  role: UserRole;
  iat?: number;
  exp?: number;
}

// Auth tokens interface
export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

// Error codes enum
export enum ErrorCode {
  VALIDATION_ERROR = "VALIDATION_ERROR",
  AUTHENTICATION_ERROR = "AUTHENTICATION_ERROR",
  AUTHORIZATION_ERROR = "AUTHORIZATION_ERROR",
  NOT_FOUND_ERROR = "NOT_FOUND_ERROR",
  CONFLICT_ERROR = "CONFLICT_ERROR",
  INTERNAL_ERROR = "INTERNAL_ERROR",
  DATABASE_ERROR = "DATABASE_ERROR",
  EXTERNAL_SERVICE_ERROR = "EXTERNAL_SERVICE_ERROR",
}
